/**
 * VAT rules for patient billing documents (invoices, estimates, receipts, statements).
 *
 * A practice charges VAT only if it is a registered VAT vendor, which we determine
 * from `Groups.vatNumber`. A non-vendor may not issue anything resembling a tax
 * invoice, so at a 0% rate the documents omit VAT entirely rather than printing 0.00.
 *
 * Line item amounts throughout the billing stack are stored VAT-INCLUSIVE, so
 * extracting VAT uses rate/(1+rate) — not rate. See splitInclusive.
 */

export const DEFAULT_VAT_RATE = 0.15;

const round2 = (value: number): number => Math.round(value * 100) / 100;

/** True when the group is a registered VAT vendor (i.e. has a VAT number on file). */
export function isVatVendor(group: any): boolean {
    return String(group?.vatNumber ?? '').trim().length > 0;
}

/** The VAT rate a group charges today: 15% for registered vendors, 0% otherwise. */
export function vatRateForGroup(group: any): number {
    return isVatVendor(group) ? DEFAULT_VAT_RATE : 0;
}

/**
 * The rate to apply to a specific document.
 *
 * Documents issued after the vatRate column was introduced carry the rate that was in
 * force when they were issued, so re-printing them stays faithful even if the practice
 * later registers (or deregisters) for VAT. Documents issued before it fall back to the
 * group's current state.
 *
 * Note the explicit finite check: a stored rate of 0 is meaningful and must not fall
 * through to the group lookup, which rules out `storedRate || vatRateForGroup(group)`.
 */
export function resolveVatRate(storedRate: any, group: any): number {
    const rate = Number(storedRate);
    return storedRate !== null && storedRate !== undefined && storedRate !== '' && Number.isFinite(rate)
        ? rate
        : vatRateForGroup(group);
}

/**
 * Split a VAT-INCLUSIVE amount into its exclusive subtotal and the VAT it contains.
 * At R115.00 incl. and 15%, that is R100.00 + R15.00 — not R97.75 + R17.25.
 * At a 0% rate the subtotal is the full amount and VAT is zero.
 */
export function splitInclusive(totalInclusive: number, rate: number): { subtotal: number; vat: number } {
    const inclusive = Number(totalInclusive) || 0;
    const vat = round2((inclusive * rate) / (1 + rate));
    return { vat, subtotal: round2(inclusive - vat) };
}

/** The VAT-exclusive equivalent of a VAT-inclusive amount. Returns it unchanged at 0%. */
export function exclusiveOf(totalInclusive: number, rate: number): number {
    return (Number(totalInclusive) || 0) / (1 + rate);
}

/** Render a rate for display: 0.15 -> '15%'. Keeps at most one decimal place. */
export function formatVatPercent(rate: number): string {
    return `${Math.round((Number(rate) || 0) * 1000) / 10}%`;
}
